import React from 'react';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, Shield, Clock, Award } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex flex-col bg-black">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1602766820852-fe338a735d97?auto=format&fit=crop&w=1920&q=80"
          alt="Dark luxury car in showroom"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50"></div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-32 md:py-40">
          <div className="max-w-2xl">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-5 py-2 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-gold-500 animate-pulse"></span>
              <span className="text-gold-500 font-semibold text-xs sm:text-sm uppercase tracking-widest">
                Chicago's Premier Studio
              </span>
            </motion.div>

            {/* Heading */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6"
            >
              <span className="text-white block">Elevate Your</span>
              <span className="text-gold-500 block italic">Vehicle's Style</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="text-gray-400 text-base sm:text-lg max-w-xl leading-relaxed mb-10"
            >
              Professional-grade window tinting that enhances privacy,
              blocks UV rays, and delivers a sleek, showroom finish.
              Lifetime warranty included.
            </motion.p>

            {/* Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="flex flex-wrap gap-4"
            >
              <a href="tel:+17736902541" data-testid="hero-call-now-btn">
                <Button className="bg-gold-500 hover:bg-gold-600 text-black font-semibold px-8 py-6 text-base rounded-md transition-all duration-300">
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </Button>
              </a>
              <Button
                variant="outline"
                data-testid="hero-view-options-btn"
                className="border-2 border-gold-500 text-gold-500 hover:bg-gold-500/10 bg-transparent px-8 py-6 text-base rounded-md transition-all duration-300"
                onClick={() => scrollToSection('options')}
              >
                View Options
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom Stats Bar */}
      <div className="relative z-10 border-t border-white/10 bg-black/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/10">
            {[
              { icon: Shield, title: 'UV Protection', desc: '99% Blocked', testId: 'stat-uv' },
              { icon: Award, title: 'Warranty', desc: 'Lifetime Guarantee', testId: 'stat-warranty' },
              { icon: Clock, title: 'Fast Install', desc: '2-4 Hours Average', testId: 'stat-install' },
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.0 + idx * 0.15 }}
                className={`flex items-center gap-4 py-6 sm:py-8 ${idx === 0 ? 'sm:pr-8' : idx === 1 ? 'sm:px-8' : 'sm:pl-8'}`}
                data-testid={item.testId}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg border border-white/15 bg-white/5 flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-gold-500" />
                </div>
                <div>
                  <div className="text-white font-semibold text-base">{item.title}</div>
                  <div className="text-gray-500 text-sm">{item.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
